import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import {
  FileText, Dog, Info, Sparkles, AlertCircle, CheckCircle2, ArrowRight, Heart, Eye, Loader2, PawPrint
} from 'lucide-react'
import { useAnalysis } from '../AnalysisContext'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import UpgradeModalReport from './UpgradeModalReport';
import { useUser } from "@clerk/clerk-react";
import { Button } from '@/components/ui/button';
import axios from 'axios';
import toast from 'react-hot-toast';
import PDFGenerationLoader from '../components/PDFGenerationLoader';
import AnalysisConfidence from './overview/AnalysisConfidence';
import PetInfoCard from './overview/PetInfoCard';
import AnalysisInsightsGrid from './overview/AnalysisInsightsGrid';
import RelevantFindings from './overview/RelevantFindings';
import AnalysisNote from './overview/AnalysisNote';
import UrgencyLevelCard from './overview/UrgencyLevelCard';
import DiagnosticosCard from './overview/DiagnosticosCard';
import { AnalysisLoading } from './commonTabs/AnalysisLoading';
import PricingModal from '../pages/PricingModal'
import { getUserPlan } from '@/utils/plans';
import { trackPixelEvent, PIXEL_EVENTS } from '../utils/pixelService.js';
import { trackReportViewed } from '../utils/gaService.js';
import { trackHotjarEvent, setHotjarState } from '../utils/hotjarService.js';


// Function to format breed name
const formatBreedName = (breedName) => {
  if (!breedName) return 'Tu Mascota';

  return breedName
    .split('_') // Split by underscore
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize first letter of each word
    .join(' '); // Join with space
};


const OverviewTab = () => {
  const { analysisData, userInput, imageUrls, petName } = useAnalysis()

  const navigate = useNavigate()
  const { user } = useUser();
  const [isExporting, setIsExporting] = useState(false);
  const hasTrackedReportView = useRef(false);

  // Track report view with Facebook Pixel - only once per component mount
  useEffect(() => {
    if (analysisData?.id && !hasTrackedReportView.current) {
      // Parámetros comunes para el evento de visualización de reportes
      const reportViewParams = {
        content_name: 'Pet Health Overview',
        content_id: analysisData.id,
        content_type: 'report',
        pet_name: petName || 'Unknown'
      };
      
      // Rastrear en Facebook Pixel
      trackPixelEvent(PIXEL_EVENTS.REPORT_VIEWED, reportViewParams);
      
      // Rastrear en Google Analytics
      trackReportViewed(analysisData.id, reportViewParams);
      
      // Rastrear en Hotjar
      trackHotjarEvent('report_viewed');
      
      // Establecer contexto para Hotjar
      setHotjarState({
        report_id: analysisData.id,
        pet_name: petName || 'Unknown',
        analysis_confidence: analysisData?.analisisGeneral?.vistaGeneral?.confianzaAnalisis || 0
      });
      
      // Mark as tracked to prevent duplicate events
      hasTrackedReportView.current = true;
    }
  }, [analysisData?.id, petName]);

  if (!analysisData) {
    return <AnalysisLoading />;
  }

  const plan = getUserPlan(user);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    analisisGeneral: {
      vistaGeneral: {
        urgenciaNivel = {},
        identificacionRaza: { razaPrincipal = {} } = {},
        edadEstimada = {},
        insightsAdicionales = [],
        oportunidadesMejora = [],
        puntosFuertes = [],
        hallazgosRelevantes = [],
        confianzaAnalisis = 0,
        estadoGeneral = '',
        nivelUrgencia = {},
        diagnosticosADescartar = [],
      } = {}
    } = {},
    metaAnalisis: {
      razaSecundaria = '',
      caracteristicasRaza: {
        confianza = ''
      },
    }
  } = analysisData || {};

  // Extract values with fallbacks
  const nombreMascota = formatBreedName(razaPrincipal?.nombre) || 'Tu Mascota';


  const coincidenciaRaza = razaPrincipal?.porcentaje || 0;
  const edad = edadEstimada?.cronologica || 'Sin dato';

  const isPremium = user?.unsafeMetadata?.isPremium;
  const [showUpgradeModalReport, setShowUpgradeModalReport] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);

  const handleExportPDF = async (event) => {
    if (!isPremium) {
      setShowUpgradeModalReport(true);
      return;
    }
    try {
      setIsExporting(true);
      setPdfProgress(0);

      // Start progress simulation
      const progressInterval = setInterval(() => {
        setPdfProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90; // Keep at 90% until actual completion
          }
          return prev + 5;
        });
      }, 500);

      const sanitizedData = analysisData;

      const validImageUrls = imageUrls?.filter(url =>
        url && (url.startsWith('http://') || url.startsWith('https://'))
      ) || [];

      const exportData = {
        analysisData: sanitizedData,
        userId: user?.id,
        userContext: userInput || 'No se proporcionó contexto adicional',
        imageUrls: validImageUrls,
        timestamp: analysisData.timestamp || new Date().toISOString()
      };

      const response = await axios.post('/api/pdf/analysis-export', exportData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 90000
      });

      if (!(response.data instanceof Blob)) {
        throw new Error('Invalid response format');
      }

      // Set to 100% when PDF is ready
      setPdfProgress(100);
      clearInterval(progressInterval);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateStr = (analysisData.timestamp || new Date().toISOString()).split('T')[0];
      link.setAttribute('download', `analisis_dogscan_${dateStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();

      toast.success('Reporte PDF descargado correctamente');
    } catch (error) {
      console.error('Export error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('La operación tardó demasiado tiempo. Por favor, intente nuevamente.');
      } else {
        toast.error('Error al exportar el análisis. Por favor, intente nuevamente.');
      }
    } finally {
      setIsExporting(false);
      setPdfProgress(0);
    }
  };
  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-4 py-3 sm:py-6 space-y-4 sm:space-y-6">
      {/* Resumen General */}
      <AnalysisConfidence confianzaAnalisis={confianzaAnalisis} />

      {/* <UrgencyIndicator urgenciaNivel={urgenciaNivel} /> */}
      {/* Información de la Mascota */}
      <PetInfoCard
        nombreMascota={nombreMascota}
        coincidenciaRaza={coincidenciaRaza}
        estadoGeneral={estadoGeneral}
        edad={edad}
        handleExportPDF={handleExportPDF}
        isExporting={isExporting}
        imageUrls={imageUrls}
        petName={petName}
        razaSecundaria={razaSecundaria}
        razaPrincipal={analysisData.metaAnalisis.razaPrincipal}
        confianza={confianza}
      />
      <UrgencyLevelCard nivelUrgencia={nivelUrgencia} />

      {diagnosticosADescartar.length > 0 && (<DiagnosticosCard diagnosticosADescartar={diagnosticosADescartar} />)}


      {/* Aspectos Positivos y Áreas de Atención */}
      <AnalysisInsightsGrid
        insightsAdicionales={insightsAdicionales}
        puntosFuertes={puntosFuertes}
        oportunidadesMejora={oportunidadesMejora}
      />

      {/* Hallazgos Importantes */}
      <RelevantFindings hallazgosRelevantes={hallazgosRelevantes} />

      {/* Nota Final */}
      <AnalysisNote />


      <PricingModal
        isOpen={showUpgradeModalReport}
        onClose={() => setShowUpgradeModalReport(false)}
        plan={plan}
      />

      {isExporting && <PDFGenerationLoader progress={pdfProgress} />}
    </div>
  )
}

export default OverviewTab